(() => {
  const root = document.querySelector('[data-h7-scratch]');
  if (!root || window.Shopify?.designMode) return;

  // Storage keys, documented so they can be cleared or audited deliberately.
  //
  //   localStorage h7ScratchDismissedAt - epoch ms the visitor closed the
  //     offer. Set once; the offer is never shown to that visitor again.
  //   localStorage h7ScratchClaimedAt   - epoch ms the visitor submitted their
  //     email. Set once; the offer is never shown to that visitor again.
  //   sessionStorage h7WelcomeOfferShown - set when the offer opens, so it
  //     appears at most once per browsing session.
  const DISMISS_KEY = 'h7ScratchDismissedAt';
  const CLAIM_KEY = 'h7ScratchClaimedAt';
  const SESSION_KEY = 'h7WelcomeOfferShown';
  const PREVIEW = new URLSearchParams(window.location.search).get('h7-scratch') === 'preview';
  const blockedPath = /^\/(cart|checkout|challenge)(\/|$)/.test(window.location.pathname);
  const readStoredTime = (key) => {
    try {
      return Number(window.localStorage.getItem(key) || 0);
    } catch {
      return 0;
    }
  };
  const storeTime = (key) => {
    try {
      window.localStorage.setItem(key, String(Date.now()));
    } catch {
      // The offer still works when storage is unavailable.
    }
  };
  const sessionSeen = () => {
    try {
      return window.sessionStorage.getItem(SESSION_KEY) === '1';
    } catch {
      return false;
    }
  };
  const markSessionSeen = () => {
    try {
      window.sessionStorage.setItem(SESSION_KEY, '1');
    } catch {
      // Storage being unavailable must not break the offer.
    }
  };
  const dismissedAt = readStoredTime(DISMISS_KEY);
  const claimedAt = readStoredTime(CLAIM_KEY);

  // Suppression: an offer on top of an offer is noise, and a visitor who came
  // from an email is already on the list, so the gift is already theirs.
  const params = new URLSearchParams(window.location.search);
  const urlCarriesDiscount = params.has('discount') || params.has('discount_code');
  const cartHasDiscount = Number(root.dataset.cartDiscounts || 0) > 0;
  const referrerHost = (() => {
    try {
      return document.referrer ? new URL(document.referrer).hostname.toLowerCase() : '';
    } catch {
      return '';
    }
  })();
  const emailReferrer =
    /(^|\.)(mail|email|webmail|outlook|hotmail|inbox)\./.test(referrerHost) ||
    /(^|\.)(klaviyomail|sendgrid|mailchimp|list-manage)\.com$/.test(referrerHost) ||
    referrerHost === 'mail.google.com' ||
    params.get('utm_medium') === 'email' ||
    params.get('utm_source') === 'klaviyo' ||
    params.has('_kx');

  if (
    !PREVIEW &&
    (blockedPath ||
      dismissedAt > 0 ||
      claimedAt > 0 ||
      sessionSeen() ||
      urlCarriesDiscount ||
      cartHasDiscount ||
      emailReferrer)
  ) {
    return;
  }

  const dialog = root.querySelector('[role="dialog"]');
  const form = root.querySelector('[data-h7-scratch-form]');
  const emailInput = root.querySelector('[data-h7-scratch-email]');
  const submitButton = root.querySelector('[data-h7-scratch-submit]');
  const errorMessage = root.querySelector('[data-h7-scratch-error]');
  const card = root.querySelector('[data-h7-scratch-card]');
  const canvas = root.querySelector('[data-h7-scratch-canvas]');
  const revealButton = root.querySelector('[data-h7-scratch-reveal]');
  const claim = root.querySelector('[data-h7-scratch-claim]');
  const success = root.querySelector('[data-h7-scratch-success]');
  const closeButtons = root.querySelectorAll('[data-h7-scratch-close]');
  const closeConfirm = root.querySelector('[data-h7-scratch-close-confirm]');
  const keepButton = root.querySelector('[data-h7-scratch-keep]');
  const leaveButton = root.querySelector('[data-h7-scratch-leave]');
  // The offer is a free gift rather than a discount, so there is no code to
  // carry, copy or apply, and nothing this popup hands out reaches the order
  // total.
  const giftName = root.dataset.giftName;
  const giftValue = root.dataset.giftValue || '';
  const copy = {
    scratchHere: root.dataset.scratchHere || 'Scratch here',
    revealedLabel: (root.dataset.revealedLabel || 'Revealed: you unlocked {gift}, free').replace(
      '{gift}',
      giftName || 'your gift',
    ),
    invalidEmail: root.dataset.invalidEmail || 'Enter a valid email address.',
    sending: root.dataset.sending || 'Sending...',
    sendFailed: root.dataset.sendFailed || 'We could not send your chapter. Please try again.',
    emailCode: root.dataset.emailCode || 'Send me chapter one',
  };
  const required = [
    dialog,
    form,
    emailInput,
    submitButton,
    errorMessage,
    card,
    canvas,
    revealButton,
    claim,
    success,
    closeConfirm,
    keepButton,
    leaveButton,
  ];
  if (required.some((element) => !element) || closeButtons.length === 0) return;
  // Refuse to arm without the Klaviyo credentials and the gift name: the popup
  // would let the visitor scratch, reveal and submit, then 400 on every send,
  // or promise a prize it cannot name.
  if (!giftName || !root.dataset.klaviyoListId || !root.dataset.klaviyoSiteId) return;

  let opened = false;
  let previousFocus = null;
  let scratching = false;
  let revealed = false;
  let coverDrawn = false;
  let closeConfirmOpen = false;
  let lastPoint = null;
  let lastCoverageCheck = 0;

  const focusableSelector = 'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])';

  const loadImage = (image) => {
    const primarySrc = image.dataset.src;
    const fallbackSrc = image.dataset.fallbackSrc;
    if (!primarySrc) return;

    let retriedPrimary = false;
    let triedFallback = false;
    const handleError = () => {
      if (!retriedPrimary) {
        retriedPrimary = true;
        const retryUrl = new URL(primarySrc, window.location.href);
        retryUrl.searchParams.set('h7-retry', String(Date.now()));
        image.src = retryUrl.href;
        return;
      }
      if (!triedFallback && fallbackSrc && fallbackSrc !== primarySrc) {
        triedFallback = true;
        image.src = fallbackSrc;
        return;
      }
      image.removeEventListener('error', handleError);
    };

    image.addEventListener('error', handleError);
    image.addEventListener('load', () => image.removeEventListener('error', handleError), { once: true });
    image.src = primarySrc;
    image.removeAttribute('data-src');
  };

  const drawCover = () => {
    if (coverDrawn) return;
    coverDrawn = true;
    const context = canvas.getContext('2d', { willReadFrequently: true });
    const image = context.createImageData(canvas.width, canvas.height);
    let seed = 7717;

    for (let y = 0; y < canvas.height; y += 1) {
      for (let x = 0; x < canvas.width; x += 1) {
        seed = (seed * 1664525 + 1013904223) >>> 0;
        const noise = ((seed >>> 24) / 255 - 0.5) * 30;
        const horizontal = x / canvas.width;
        const vertical = y / canvas.height;
        const sheen = Math.max(0, 1 - Math.abs(horizontal - vertical * 0.4 - 0.34) * 4.4) * 34;
        const index = (y * canvas.width + x) * 4;
        image.data[index] = Math.max(0, Math.min(255, 66 + horizontal * 109 + vertical * 10 + noise + sheen));
        image.data[index + 1] = Math.max(0, Math.min(255, 205 - horizontal * 108 + vertical * 12 + noise + sheen));
        image.data[index + 2] = Math.max(0, Math.min(255, 219 + horizontal * 18 + vertical * 20 + noise + sheen));
        image.data[index + 3] = 255;
      }
    }
    context.putImageData(image, 0, 0);

    const glow = context.createRadialGradient(180, 80, 10, 220, 140, 330);
    glow.addColorStop(0, 'rgba(255, 255, 255, 0.36)');
    glow.addColorStop(1, 'rgba(255, 255, 255, 0)');
    context.fillStyle = glow;
    context.fillRect(0, 0, canvas.width, canvas.height);

    context.save();
    context.globalAlpha = 0.13;
    context.strokeStyle = '#ffffff';
    context.lineWidth = 13;
    for (let line = -220; line < canvas.width + 220; line += 46) {
      context.beginPath();
      context.moveTo(line, canvas.height);
      context.lineTo(line + 230, 0);
      context.stroke();
    }
    context.restore();

    context.save();
    context.translate(canvas.width / 2, canvas.height / 2 + 4);
    context.rotate(-0.025);
    const gold = context.createLinearGradient(0, -48, 0, 48);
    gold.addColorStop(0, '#fff4ad');
    gold.addColorStop(0.42, '#f7c84f');
    gold.addColorStop(0.68, '#e6a92c');
    gold.addColorStop(1, '#fff0a0');
    context.fillStyle = gold;
    context.strokeStyle = 'rgba(75, 25, 88, 0.72)';
    context.lineWidth = 3;
    context.shadowColor = 'rgba(55, 14, 82, 0.78)';
    context.shadowBlur = 15;
    context.shadowOffsetY = 6;
    context.textAlign = 'center';
    context.textBaseline = 'middle';
    context.font = '700 82px "Segoe Print", "Bradley Hand", "Chalkboard SE", cursive';
    context.strokeText(copy.scratchHere, 0, 0);
    context.fillText(copy.scratchHere, 0, 0);
    context.restore();
  };

  const open = async () => {
    if (opened) return;
    await window.H7OverlayCoordinator?.request('h7-scratch', { priority: 10 });
    if (opened) return;
    opened = true;
    if (!PREVIEW) markSessionSeen();
    try {
      previousFocus = document.activeElement;
      root.querySelectorAll('img[data-src]').forEach(loadImage);
      drawCover();
      root.hidden = false;
      document.body.classList.add('h7-scratch-open');
      window.requestAnimationFrame(() => card.focus());
    } catch (error) {
      // A blocked canvas (privacy settings) throws in drawCover; release the
      // overlay lock or the currency prompt and account gateway stay frozen
      // behind a popup that never rendered.
      opened = false;
      root.hidden = true;
      document.body.classList.remove('h7-scratch-open');
      window.H7OverlayCoordinator?.release('h7-scratch');
    }
  };

  const dismiss = () => {
    if (!opened) return;
    opened = false;
    closeConfirmOpen = false;
    closeConfirm.hidden = true;
    root.hidden = true;
    document.body.classList.remove('h7-scratch-open');
    window.H7OverlayCoordinator?.release('h7-scratch');
    if (!PREVIEW) storeTime(DISMISS_KEY);
    previousFocus?.focus?.();
  };

  const hideCloseConfirm = () => {
    closeConfirmOpen = false;
    closeConfirm.hidden = true;
    closeButtons[0]?.focus();
  };

  const requestClose = () => {
    if (revealed && success.hidden) {
      closeConfirmOpen = true;
      closeConfirm.hidden = false;
      window.requestAnimationFrame(() => keepButton.focus());
      return;
    }
    dismiss();
  };

  const trapFocus = (event) => {
    if (!opened || event.key !== 'Tab') return;
    const focusRoot = closeConfirmOpen ? closeConfirm : dialog;
    const focusable = Array.from(focusRoot.querySelectorAll(focusableSelector)).filter(
      (element) => !element.closest('[hidden]'),
    );
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  const showError = (message) => {
    errorMessage.textContent = message;
    errorMessage.hidden = false;
    emailInput.setAttribute('aria-invalid', 'true');
  };

  emailInput.addEventListener('input', () => {
    if (!emailInput.checkValidity()) return;
    emailInput.removeAttribute('aria-invalid');
    errorMessage.hidden = true;
  });

  const subscriptionPayload = (email) => ({
    data: {
      type: 'subscription',
      attributes: {
        custom_source: 'Hour Seven scratch card',
        profile: {
          data: {
            type: 'profile',
            attributes: {
              email,
              properties: {
                'Signup source': 'Hour Seven scratch card',
                // Read by the welcome flow, which sends the gift rather than a
                // code. Renaming either key empties the email silently.
                'Scratch card prize': giftName,
                'Welcome gift': giftName,
                'Welcome gift value': giftValue,
              },
              subscriptions: {
                email: {
                  marketing: { consent: 'SUBSCRIBED' },
                },
              },
            },
          },
        },
      },
      relationships: {
        list: {
          data: {
            type: 'list',
            id: root.dataset.klaviyoListId,
          },
        },
      },
    },
  });

  const reveal = () => {
    if (revealed) return;
    revealed = true;
    card.classList.add('is-revealed');
    card.setAttribute('aria-label', copy.revealedLabel);
    revealButton.hidden = true;
    claim.hidden = false;
    window._learnq = window._learnq || [];
    window._learnq.push(['track', 'Scratch Card Revealed', { 'Welcome gift': giftName }]);
    window.setTimeout(() => emailInput.focus(), 450);
  };

  const coverage = () => {
    const context = canvas.getContext('2d', { willReadFrequently: true });
    const pixels = context.getImageData(0, 0, canvas.width, canvas.height).data;
    let transparent = 0;
    let sampled = 0;
    for (let index = 3; index < pixels.length; index += 64) {
      sampled += 1;
      if (pixels[index] < 48) transparent += 1;
    }
    return transparent / sampled;
  };

  const pointFromEvent = (event) => {
    const rect = canvas.getBoundingClientRect();
    return {
      x: ((event.clientX - rect.left) / rect.width) * canvas.width,
      y: ((event.clientY - rect.top) / rect.height) * canvas.height,
    };
  };

  const scratchAt = (point) => {
    const context = canvas.getContext('2d', { willReadFrequently: true });
    context.globalCompositeOperation = 'destination-out';
    context.lineCap = 'round';
    context.lineJoin = 'round';
    context.lineWidth = 64;
    context.beginPath();
    if (lastPoint) context.moveTo(lastPoint.x, lastPoint.y);
    else context.moveTo(point.x, point.y);
    context.lineTo(point.x, point.y);
    context.stroke();
    lastPoint = point;

    const now = performance.now();
    if (now - lastCoverageCheck > 160) {
      lastCoverageCheck = now;
      if (coverage() >= 0.46) reveal();
    }
  };

  form.addEventListener('submit', async (event) => {
    event.preventDefault();
    errorMessage.hidden = true;
    if (!emailInput.checkValidity()) {
      showError(copy.invalidEmail);
      emailInput.focus();
      return;
    }

    const email = emailInput.value.trim();
    submitButton.disabled = true;
    submitButton.textContent = copy.sending;
    form.setAttribute('aria-busy', 'true');
    try {
      const response = await fetch(
        `https://a.klaviyo.com/client/subscriptions?company_id=${encodeURIComponent(root.dataset.klaviyoSiteId)}`,
        {
          method: 'POST',
          headers: {
            accept: 'application/vnd.api+json',
            'content-type': 'application/vnd.api+json',
            revision: '2026-04-15',
          },
          body: JSON.stringify(subscriptionPayload(email)),
          signal: AbortSignal.timeout?.(10000),
        },
      );
      if (!response.ok) throw new Error('Subscription failed');

      emailInput.removeAttribute('aria-invalid');
      claim.hidden = true;
      success.hidden = false;
      if (!PREVIEW) storeTime(CLAIM_KEY);
      window._learnq = window._learnq || [];
      window._learnq.push(['identify', { $email: email }]);
      window._learnq.push([
        'track',
        'Scratch Card Claimed',
        { 'Welcome gift': giftName, 'Welcome gift value': giftValue },
      ]);
    } catch (error) {
      showError(copy.sendFailed);
      submitButton.disabled = false;
      submitButton.textContent = copy.emailCode;
    } finally {
      form.removeAttribute('aria-busy');
    }
  });

  canvas.addEventListener('pointerdown', (event) => {
    if (revealed) return;
    scratching = true;
    lastPoint = null;
    canvas.setPointerCapture(event.pointerId);
    scratchAt(pointFromEvent(event));
  });

  canvas.addEventListener('pointermove', (event) => {
    if (!scratching || revealed) return;
    scratchAt(pointFromEvent(event));
  });

  const stopScratching = (event) => {
    scratching = false;
    lastPoint = null;
    if (event?.pointerId != null && canvas.hasPointerCapture(event.pointerId)) canvas.releasePointerCapture(event.pointerId);
  };

  canvas.addEventListener('pointerup', stopScratching);
  canvas.addEventListener('pointercancel', stopScratching);
  canvas.addEventListener('pointerleave', stopScratching);
  card.addEventListener('keydown', (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      reveal();
    }
  });

  revealButton.addEventListener('click', reveal);
  // Backdrop click closes, but only a click that both starts and ends on the
  // backdrop, so a drag out of the dialog does not dismiss it.
  root.addEventListener('pointerdown', (event) => {
    root.dataset.h7BackdropPress = event.target === root ? 'true' : 'false';
  });
  root.addEventListener('click', (event) => {
    if (event.target === root && root.dataset.h7BackdropPress === 'true') requestClose();
  });
  closeButtons.forEach((button) => button.addEventListener('click', requestClose));
  keepButton.addEventListener('click', hideCloseConfirm);
  leaveButton.addEventListener('click', dismiss);

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      if (closeConfirmOpen) hideCloseConfirm();
      else requestClose();
    }
    trapFocus(event);
  });

  if (PREVIEW) {
    open();
    return;
  }

  // Trigger rules, whichever fires first:
  //   desktop - exit intent, or 25 seconds
  //   mobile  - 50% scroll depth, or 25 seconds
  //
  // Every route is gated on the visitor having scrolled past the purchase
  // ladder. Cold paid traffic must read the price ladder before anything
  // interrupts it, so on a page that has a ladder the offer stays shut until
  // the ladder's bottom edge has left the viewport.
  const DWELL_MS = 25000;
  const SCROLL_DEPTH = 0.5;
  const isDesktop = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  const selectorAnchor = root.dataset.selectorAnchor
    ? document.querySelector(root.dataset.selectorAnchor)
    : null;

  // No ladder on this page (anything that is not the product page) means
  // nothing to read past, so the gate is open.
  const pastSelector = () => {
    if (!selectorAnchor) return true;
    return selectorAnchor.getBoundingClientRect().bottom < 0;
  };

  const scrolledHalfway = () => {
    const scrollable = document.documentElement.scrollHeight - window.innerHeight;
    if (scrollable <= 0) return true;
    return window.scrollY / scrollable >= SCROLL_DEPTH;
  };

  let dwellTimer = 0;
  let armed = true;

  const disarm = () => {
    armed = false;
    window.clearTimeout(dwellTimer);
    window.removeEventListener('scroll', onScroll);
    document.removeEventListener('mouseout', onExitIntent);
  };

  // A trigger that fires before the ladder has been read is remembered rather
  // than dropped: the offer opens on the first scroll that clears the gate.
  let pending = false;
  const tryOpen = () => {
    if (!armed) return;
    if (!pastSelector()) {
      pending = true;
      return;
    }
    disarm();
    open();
  };

  function onScroll() {
    if (!armed) return;
    if (pending) {
      tryOpen();
      return;
    }
    if (!isDesktop && scrolledHalfway()) tryOpen();
  }

  function onExitIntent(event) {
    if (!isDesktop || !armed) return;
    if (event.clientY <= 0 && !event.relatedTarget) tryOpen();
  }

  window.addEventListener('scroll', onScroll, { passive: true });
  document.addEventListener('mouseout', onExitIntent);
  dwellTimer = window.setTimeout(tryOpen, DWELL_MS);
})();
